import { useCallback, useEffect, useState } from "react";
import { PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, GST_RATES, inr } from "../lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const emptyForm = { name: "", hsn: "", unit: "pcs", sale_price: "", purchase_price: "", gst_rate: "18", opening_stock: "0", low_stock: "5", batch_no: "", mfg_date: "", exp_date: "" };

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const pharma = settings?.industry === "pharma";

  const load = useCallback(async () => {
    try {
      const [it, st] = await Promise.all([API.get("/items"), API.get("/settings")]);
      setItems(it.data);
      setSettings(st.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name, hsn: it.hsn || "", unit: it.unit || "pcs",
      sale_price: String(it.sale_price), purchase_price: String(it.purchase_price),
      gst_rate: String(it.gst_rate), opening_stock: "0", low_stock: String(it.low_stock),
      batch_no: it.batch_no || "", mfg_date: it.mfg_date || "", exp_date: it.exp_date || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Item name required");
    setSaving(true);
    const payload = {
      name: form.name, hsn: form.hsn, unit: form.unit,
      sale_price: Number(form.sale_price) || 0, purchase_price: Number(form.purchase_price) || 0,
      gst_rate: Number(form.gst_rate) || 0, opening_stock: Number(form.opening_stock) || 0,
      low_stock: Number(form.low_stock) || 0,
      batch_no: form.batch_no || "", mfg_date: form.mfg_date || "", exp_date: form.exp_date || "",
    };
    try {
      if (editing) {
        await API.put(`/items/${editing.id}`, payload);
        toast.success("Item updated");
      } else {
        await API.post("/items", payload);
        toast.success("Item added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await API.delete(`/items/${id}`);
      toast.success("Item deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Inventory</h1>
          <p className="text-sm text-muted-foreground">Products &amp; services with HSN codes and live stock</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="new-item-button"><Plus size={16} className="mr-1" weight="bold" /> New Item</Button>
          </DialogTrigger>
          <DialogContent data-testid="item-form-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">{editing ? "Edit Item" : "New Item"}</DialogTitle>
              <DialogDescription className="sr-only">Inventory item details form</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Item Name</Label>
                <Input value={form.name} onChange={set("name")} placeholder="e.g. Wireless Mouse" data-testid="item-name-input" />
              </div>
              <div className="space-y-2">
                <Label>HSN Code</Label>
                <Input value={form.hsn} onChange={set("hsn")} placeholder="Optional" data-testid="item-hsn-input" />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input value={form.unit} onChange={set("unit")} data-testid="item-unit-input" />
              </div>
              <div className="space-y-2">
                <Label>Sale Price (₹)</Label>
                <Input type="number" min="0" step="any" value={form.sale_price} onChange={set("sale_price")} data-testid="item-sale-price-input" />
              </div>
              <div className="space-y-2">
                <Label>Purchase Price (₹)</Label>
                <Input type="number" min="0" step="any" value={form.purchase_price} onChange={set("purchase_price")} data-testid="item-purchase-price-input" />
              </div>
              <div className="space-y-2">
                <Label>GST %</Label>
                <Select value={form.gst_rate} onValueChange={set("gst_rate")}>
                  <SelectTrigger data-testid="item-gst-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GST_RATES.map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {!editing && (
                <div className="space-y-2">
                  <Label>Opening Stock</Label>
                  <Input type="number" step="any" value={form.opening_stock} onChange={set("opening_stock")} data-testid="item-stock-input" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Low Stock Alert At</Label>
                <Input type="number" step="any" value={form.low_stock} onChange={set("low_stock")} data-testid="item-low-stock-input" />
              </div>
              {pharma && (
                <>
                  <div className="space-y-2">
                    <Label>Batch No.</Label>
                    <Input value={form.batch_no} onChange={set("batch_no")} placeholder="e.g. B123" data-testid="item-batch-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Mfg Date</Label>
                    <Input type="date" value={form.mfg_date} onChange={set("mfg_date")} data-testid="item-mfg-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Expiry Date</Label>
                    <Input type="date" value={form.exp_date} onChange={set("exp_date")} data-testid="item-exp-input" />
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="item-cancel-button">Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="item-save-button">{saving ? "Saving…" : editing ? "Update" : "Add Item"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border bg-card">
        {items.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-state">
            <p className="font-heading text-lg font-semibold">No items yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Add products or services — invoices will auto-fill prices, HSN and GST rates.</p>
          </div>
        ) : (
          <Table data-testid="items-table">
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>HSN</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead className="text-right">Sale Price</TableHead>
                <TableHead className="text-right">Purchase Price</TableHead>
                <TableHead className="text-right">GST %</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} className="transition-colors hover:bg-muted/50" data-testid={`item-row-${it.name}`}>
                  <TableCell className="font-medium">{it.name}</TableCell>
                  <TableCell>{it.hsn || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{it.stock} {it.unit}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(it.sale_price)}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(it.purchase_price)}</TableCell>
                  <TableCell className="text-right tabular-nums">{it.gst_rate}%</TableCell>
                  <TableCell>
                    {it.stock <= it.low_stock ? (
                      <Badge variant="outline" className="border-[#E02424]/30 bg-[#E02424]/10 text-[#E02424]" data-testid={`low-stock-badge-${it.name}`}>Low stock</Badge>
                    ) : (
                      <Badge variant="outline" className="border-[#008A52]/30 bg-[#008A52]/10 text-[#008A52]">In stock</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(it)} data-testid={`item-edit-${it.name}`}>
                        <PencilSimple size={16} className="text-muted-foreground" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => del(it.id)} data-testid={`item-delete-${it.name}`}>
                        <Trash size={16} className="text-muted-foreground" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
